import React, { useEffect, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { getCurrentUser, logout } from "../services/authApi";
import { getDashboardIntegration, getDashboardOverview, provisionDashboardIntegration } from "../services/dashboardApi";

const MasterLayout = ({ children }) => {
  const navigate = useNavigate();

  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [sessionStatus, setSessionStatus] = useState("checking");
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [showConnectBanner, setShowConnectBanner] = useState(false);
  const [dashboardTenantName, setDashboardTenantName] = useState("");
  const [dashboardBrandId, setDashboardBrandId] = useState("");
  const [dashboardIntegration, setDashboardIntegration] = useState(null);
  const [generatingApiKeys, setGeneratingApiKeys] = useState(false);
  const [generatedApiKeys, setGeneratedApiKeys] = useState(null);
  const [apiKeyError, setApiKeyError] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  const refreshIntegration = useCallback(async () => {
    const response = await getDashboardIntegration();
    const integration = response?.integration || null;
    setDashboardIntegration(integration);
    setShowConnectBanner(
      Boolean(integration) &&
      !(String(integration.status || "").toLowerCase() === "active" && integration.connected === true)
    );
    return integration;
  }, []);

  useEffect(() => {
    const onKeysUpdated = () => {
      // Credentials are fetched from the authenticated backend, never browser storage.
      setGeneratedApiKeys(null);
      refreshIntegration().catch(() => setApiKeyError("Could not refresh API credentials."));
    };
    window.addEventListener("simplif-api-keys-updated", onKeysUpdated);
    return () => window.removeEventListener("simplif-api-keys-updated", onKeysUpdated);
  }, [refreshIntegration]);

  const copyKey = async (value, kind) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(kind);
      setApiKeyError("");
    } catch {
      setApiKeyError("Copy failed. Please allow clipboard access.");
    }
  };

  /* =====================================================
     SESSION
  ===================================================== */

  useEffect(() => {
    let active = true;

    const checkSession = async () => {
      try {
        const response = await getCurrentUser();

        if (!active) {
          return;
        }

        setCurrentUser(response?.user || null);
        setSessionStatus("authenticated");
      } catch (requestError) {
        if (!active) {
          return;
        }

        if (
          requestError.status === 401 ||
          requestError.code === "AUTH_REQUIRED"
        ) {
          setSessionStatus("unauthenticated");
          navigate("/sign-in", { replace: true });
          return;
        }

        if (
          requestError.status === 403 ||
          requestError.code === "ACCOUNT_UNAVAILABLE"
        ) {
          setSessionStatus("unavailable");
          return;
        }

        setSessionStatus("error");
      }
    };

    checkSession();

    return () => {
      active = false;
    };
  }, [navigate]);

  /* =====================================================
     GLOBAL CONNECT / SAMPLE-DATA STATE
  ===================================================== */

  useEffect(() => {
    if (sessionStatus !== "authenticated") {
      return undefined;
    }

    let active = true;

    const checkDashboardState = async () => {
      try {
        const [overviewResponse, integrationResponse] = await Promise.all([
          getDashboardOverview(),
          getDashboardIntegration(),
        ]);
        if (!active) return;
        const integration = integrationResponse?.integration || null;
        setDashboardTenantName(overviewResponse?.tenant?.name || "");
        setDashboardBrandId(overviewResponse?.tenant?.brandId || "");
        setDashboardIntegration(integration);
        setShowConnectBanner(
          Boolean(integration) &&
          !(String(integration.status || "").toLowerCase() === "active" && integration.connected === true)
        );
        setApiKeyError("");
      } catch {
        if (!active) return;
        // Network failures must not be interpreted as disconnected/sample mode.
        setShowConnectBanner(false);
        setApiKeyError("Unable to load integration status. Refresh to retry.");
      }
    };

    checkDashboardState();

    return () => {
      active = false;
    };
  }, [sessionStatus]);

  const handleGenerateApiKeys = async () => {
    if (generatingApiKeys) return;

    setGeneratingApiKeys(true);
    setApiKeyError("");

    try {
      const response = await provisionDashboardIntegration();
      const nextIntegration = response?.integration || null;
      const secretKey = nextIntegration?.secretKey || response?.secretKey || null;
      const publicKey = nextIntegration?.publicKey || response?.publicKey || null;

      if (!secretKey || !publicKey) {
        throw new Error("API credentials were not returned.");
      }

      setDashboardIntegration(nextIntegration);
      const latestKeys = { publicKey, secretKey };
      setGeneratedApiKeys(latestKeys);
      await refreshIntegration();
    } catch (error) {
      setApiKeyError(error?.message || "Unable to generate API keys.");
    } finally {
      setGeneratingApiKeys(false);
    }
  };

  /* =====================================================
     LOGOUT
  ===================================================== */

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);

    try {
      await logout();
    } catch (requestError) {
      if (
        requestError.status !== 401 &&
        requestError.code !== "AUTH_REQUIRED"
      ) {
        setIsLoggingOut(false);
        return;
      }
    }

    setCurrentUser(null);
    setSessionStatus("unauthenticated");
    setShowConnectBanner(false);
    setDashboardTenantName("");
    setDashboardIntegration(null);
    setGeneratedApiKeys(null);
    sessionStorage.removeItem("simplifLatestApiKeys");

    navigate("/sign-in", { replace: true });
  };

  /* =====================================================
     SESSION STATES
  ===================================================== */

  if (
    sessionStatus === "checking" ||
    sessionStatus === "unauthenticated"
  ) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        Checking your session...
      </div>
    );
  }

  if (sessionStatus === "unavailable") {
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          textAlign: "center",
        }}
      >
        This account is currently unavailable. Please contact support.
      </div>
    );
  }

  if (sessionStatus === "error") {
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          textAlign: "center",
        }}
      >
        We could not verify your session. Please refresh and try again.
      </div>
    );
  }

  /* =====================================================
     LAYOUT CONTROLS
  ===================================================== */

  const sidebarControl = () => {
    setSidebarActive((current) => !current);
  };

  const mobileMenuControl = () => {
    setMobileMenu((current) => !current);
  };

  const closeMobileMenu = () => {
    setMobileMenu(false);
  };

  const publicKey = dashboardIntegration?.publicKey || generatedApiKeys?.publicKey || "";
  const secretKey = dashboardIntegration?.secretKey || generatedApiKeys?.secretKey || "";
  const maskKey = (value) => value ? `${value.slice(0, 10)}********${value.slice(-6)}` : "Not available";

  const navLinkClass = ({ isActive }) =>
    isActive ? "active-page" : "";

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>
      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      <aside
        className={
          sidebarActive
            ? "sidebar active"
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        {/* MOBILE CLOSE */}

        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
          aria-label="Close sidebar"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>

        {/* =====================================================
            SIMPLIF LOGO
        ===================================================== */}

        <div className="simplif-sidebar-brand">
          <Link
            to="/"
            onClick={closeMobileMenu}
            aria-label="Simplif home"
            className="simplif-brand-link"
          >
            <img
              src="/assets/images/simplif/simplif-logo.png"
              alt="Simplif"
              className="simplif-brand-logo"
            />
          </Link>
        </div>

        {/* =====================================================
            SIDEBAR CONTENT
        ===================================================== */}

        <div className="simplif-sidebar-content">
          <div className="sidebar-menu-area">
            <ul className="sidebar-menu" id="sidebar-menu">
              {/* OVERVIEW */}

              <li>
                <NavLink
                  to="/dashboard"
                  end
                  className={navLinkClass}
                  onClick={closeMobileMenu}
                >
                  <Icon
                    icon="solar:home-2-outline"
                    className="menu-icon"
                  />
                  <span>Overview</span>
                </NavLink>
              </li>

              {/* FRAUD */}

              <li className="sidebar-menu-group-title">
                FRAUD
              </li>

              <li>
                <NavLink
                  to="/events"
                  className={navLinkClass}
                  onClick={closeMobileMenu}
                >
                  <Icon
                    icon="solar:shield-warning-outline"
                    className="menu-icon"
                  />
                  <span>Events</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/reviews"
                  className={navLinkClass}
                  onClick={closeMobileMenu}
                >
                  <Icon
                    icon="solar:clipboard-check-outline"
                    className="menu-icon"
                  />
                  <span>Reviews</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/rules"
                  className={navLinkClass}
                  onClick={closeMobileMenu}
                >
                  <Icon
                    icon="solar:bolt-outline"
                    className="menu-icon"
                  />
                  <span>Rules</span>
                </NavLink>
              </li>

              {/* DEVELOPERS */}

              <li className="sidebar-menu-group-title">
                DEVELOPERS
              </li>

              <li>
                <NavLink
                  to="/developers"
                  className={navLinkClass}
                  onClick={closeMobileMenu}
                >
                  <Icon
                    icon="solar:code-square-outline"
                    className="menu-icon"
                  />
                  <span>Developers</span>
                </NavLink>
              </li>

              {/* ACCOUNT */}

              <li className="sidebar-menu-group-title">
                ACCOUNT
              </li>

              <li>
                <NavLink
                  to="/settings"
                  className={navLinkClass}
                  onClick={closeMobileMenu}
                >
                  <Icon
                    icon="solar:settings-outline"
                    className="menu-icon"
                  />
                  <span>Settings</span>
                </NavLink>
              </li>
            </ul>
          </div>

          {/* ===================================================
              SUPPORT - BOTTOM LEFT
          =================================================== */}

          <div className="simplif-sidebar-support">
            <div className="simplif-support-icon">
              <Icon icon="solar:question-circle-outline" />
            </div>

            <div className="simplif-support-copy">
              <div className="simplif-support-title">
                Need help or support?
              </div>

              <a
                href="mailto:support@simplif.com"
                className="simplif-support-email"
              >
                support@simplif.com
              </a>
            </div>
          </div>
        </div>
      </aside>

      {/* =====================================================
          MAIN
      ===================================================== */}

      <main
        className={
          sidebarActive
            ? "dashboard-main active"
            : "dashboard-main"
        }
      >
        {/* =====================================================
            TOP NAVBAR
        ===================================================== */}

        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            {/* LEFT */}

            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                {/* DESKTOP SIDEBAR TOGGLE */}

                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                  aria-label="Toggle sidebar"
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active"
                    />
                  )}
                </button>

                {/* MOBILE SIDEBAR TOGGLE */}

                <button
                  type="button"
                  className="sidebar-mobile-toggle"
                  onClick={mobileMenuControl}
                  aria-label="Open sidebar"
                >
                  <Icon
                    icon="heroicons:bars-3-solid"
                    className="icon"
                  />
                </button>

                {/* SEARCH */}

                <form
                  className="navbar-search"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <input
                    type="text"
                    name="search"
                    placeholder="Search"
                    autoComplete="off"
                  />

                  <Icon
                    icon="ion:search-outline"
                    className="icon"
                  />
                </form>
              </div>
            </div>

            {/* =================================================
                RIGHT - ACCOUNT ONLY
            ================================================= */}

            <div className="col-auto">
              <div className="d-flex align-items-center">
                <div className="dropdown">
                  <button
                    type="button"
                    className="simplif-business-trigger"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="Account menu"
                  >
                    <span className="simplif-business-icon">
                      <Icon
                        icon="solar:buildings-2-outline"
                        className="text-xl"
                      />
                    </span>
                  </button>

                  {/* ACCOUNT DROPDOWN */}

                  <div className="dropdown-menu to-top dropdown-menu-sm simplif-account-menu">
                    <ul className="to-top-list">
                      <li className="px-0 pt-4 pb-10 mb-6 border-bottom">
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6b7280",
                            marginBottom: "2px",
                          }}
                        >
                          Brand ID
                        </div>

                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          {dashboardBrandId || "Loading..."}
                        </div>
                      </li>

                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent d-flex align-items-center gap-3"
                          to="/settings"
                        >
                          <Icon
                            icon="solar:settings-outline"
                            className="icon text-xl"
                          />
                          Settings
                        </Link>
                      </li>

                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3"
                          onClick={handleLogout}
                          disabled={isLoggingOut}
                        >
                          <Icon
                            icon="lucide:power"
                            className="icon text-xl"
                          />
                          {isLoggingOut
                            ? "Logging Out..."
                            : "Log Out"}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            PAGE CONTENT
        ===================================================== */}

        <div className="dashboard-main-body">
          {showConnectBanner && (
            <div
              role="status"
              className="simplif-connect-banner"
            >
              <div className="simplif-connect-banner-content">
                {!dashboardIntegration?.provisionedAt && !generatedApiKeys ? (
                  <>
                    <div className="simplif-connect-banner-message">
                      <span className="simplif-connect-banner-icon">
                        <Icon icon="solar:plug-circle-bold" />
                      </span>
                      <div className="simplif-connect-banner-copy">
                        <div className="simplif-connect-banner-title">Connect Simplif to start protecting real signups</div>
                        <div className="simplif-connect-banner-description">You're currently viewing sample data{dashboardTenantName ? ` for ${dashboardTenantName}` : ""}. Connect your API to start analysing real signups and receive live fraud decisions.</div>
                      </div>
                    </div>
                    <button type="button" className="simplif-connect-banner-button" onClick={handleGenerateApiKeys} disabled={generatingApiKeys}>
                      <Icon icon="solar:key-bold" />
                      <span>{generatingApiKeys ? "Generating..." : "Generate API Keys"}</span>
                    </button>
                  </>
                ) : (
                  <div className="simplif-api-ready">
                    <div className="simplif-api-ready-top">
                      <div className="simplif-api-ready-title">
                        <span className="simplif-api-ready-check"><Icon icon="solar:check-circle-bold" /></span>
                        <div>
                          <strong>API credentials generated</strong>
                          <span>Use these credentials to connect Simplif to your signup flow.</span>
                        </div>
                      </div>
                      <div className="simplif-api-ready-actions">
                        <span className="simplif-connect-banner-waiting">Waiting for connection</span>
                        <button type="button" className="simplif-connect-banner-button" onClick={() => navigate("/developers#api-integration")}>View API Docs</button>
                      </div>
                    </div>
                    <div className="simplif-api-ready-grid">
                      <div className="simplif-api-ready-item">
                        <span>Endpoint</span>
                        <code>POST /v1/signups/evaluate</code>
                      </div>
                      {publicKey && (
                        <div className="simplif-api-ready-item">
                          <span>Public key</span>
                          <div className="simplif-api-key-row"><code>{maskKey(publicKey)}</code><button type="button" onClick={() => copyKey(publicKey, "public")}>{copiedKey === "public" ? "Copied!" : "Copy"}</button></div>
                        </div>
                      )}
                      <div className="simplif-api-ready-item simplif-api-secret">
                        <span>Secret key</span>
                        <div className="simplif-api-key-row">
                          <code>{secretKey ? maskKey(secretKey) : "Secret unavailable — rotate in Developers"}</code>
                          {secretKey && <button type="button" onClick={() => copyKey(secretKey, "secret")}>{copiedKey === "secret" ? "Copied!" : "Copy"}</button>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {apiKeyError && <div className="simplif-connect-banner-error">{apiKeyError}</div>}
              </div>
            </div>
          )}

          {children || <Outlet />}
        </div>
      </main>

      {/* =====================================================
          SIMPLIF STYLES
      ===================================================== */}

      <style>
        {`
          :root {
            --simplif-orange: #ff5a00;
            --simplif-orange-dark: #e95000;
            --simplif-orange-soft: #fff1e8;
          }

          /* ==========================================
             GLOBAL CONNECT BANNER
          ========================================== */

          .simplif-connect-banner {
            background: #111111 !important;
            color: #ffffff;
            position: relative;
            overflow: hidden;
            margin-bottom: 24px;
            padding: 20px 22px;
            border: 1px solid rgba(255, 255, 255, 0.28);
            border-radius: 12px;
            background:
              linear-gradient(
                135deg,
                #171717 0%,
                #0d0d0d 50%,
                #000000 100%
              );
            box-shadow:
              0 12px 30px rgba(0, 0, 0, 0.22),
              0 3px 8px rgba(17, 24, 39, 0.08);
          }

          .simplif-connect-banner::after {
            content: "";
            position: absolute;
            width: 220px;
            height: 220px;
            right: -90px;
            top: -130px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.09);
            pointer-events: none;
          }

          .simplif-connect-banner-content {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
          }

          .simplif-connect-banner-message {
            display: flex;
            align-items: center;
            gap: 14px;
            min-width: 0;
          }

          .simplif-connect-banner-icon {
            width: 52px;
            height: 52px;
            min-width: 52px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.18);
            border: 1px solid rgba(255, 255, 255, 0.24);
            color: #ffffff;
            font-size: 28px;
          }

          .simplif-connect-banner-copy {
            min-width: 0;
          }

          .simplif-connect-banner-title {
            color: #ffffff;
            font-size: 18px;
            font-weight: 800;
            line-height: 1.3;
            letter-spacing: -0.01em;
          }

          .simplif-connect-banner-description {
            margin-top: 5px;
            max-width: 820px;
            color: rgba(255, 255, 255, 0.94);
            font-size: 14px;
            font-weight: 500;
            line-height: 1.5;
          }

          .simplif-connect-banner-button {
            min-height: 44px;
            padding: 10px 18px;
            border: 2px solid #ffffff;
            border-radius: 8px;
            background: #ffffff;
            color: var(--simplif-orange-dark);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            flex-shrink: 0;
            font-size: 14px;
            font-weight: 800;
            line-height: 1;
            white-space: nowrap;
            cursor: pointer;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.14);
            transition:
              transform 0.15s ease,
              box-shadow 0.15s ease,
              background-color 0.15s ease;
          }

          .simplif-connect-banner-button:hover {
            background: #fff7f2;
            color: var(--simplif-orange-dark);
            transform: translateY(-1px);
            box-shadow: 0 7px 18px rgba(0, 0, 0, 0.18);
          }

          .simplif-connect-banner-button:focus-visible {
            outline: 3px solid rgba(255, 255, 255, 0.7);
            outline-offset: 3px;
          }

          .simplif-connect-banner-button-icon {
            font-size: 18px;
          }

          .simplif-connect-banner-arrow {
            font-size: 17px;
          }

          .simplif-api-ready { width: 100%; }
          .simplif-api-ready-top { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
          .simplif-api-ready-title { display: flex; align-items: center; gap: 12px; }
          .simplif-api-ready-title strong { display: block; color: #fff; font-size: 16px; }
          .simplif-api-ready-title span:not(.simplif-api-ready-check) { display: block; margin-top: 2px; color: rgba(255,255,255,.72); font-size: 13px; }
          .simplif-api-ready-check { display: flex; font-size: 28px; color: #45d483; }
          .simplif-api-ready-actions { display: flex; align-items: center; gap: 12px; }
          .simplif-connect-banner-waiting { padding: 7px 11px; border-radius: 999px; background: rgba(255,255,255,.1); color: #fff; font-size: 12px; font-weight: 700; white-space: nowrap; }
          .simplif-api-ready-grid { display: grid; grid-template-columns: .9fr 1.25fr 1.25fr; gap: 10px; margin-top: 16px; }
          .simplif-api-ready-item { min-width: 0; padding: 11px 13px; border: 1px solid rgba(255,255,255,.13); border-radius: 9px; background: rgba(255,255,255,.055); }
          .simplif-api-ready-item > span { display: block; margin-bottom: 5px; color: rgba(255,255,255,.6); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
          .simplif-api-ready-item code { color: #fff; font-size: 12px; white-space: nowrap; }
          .simplif-api-key-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; }
          .simplif-api-key-row code { overflow: hidden; text-overflow: ellipsis; }
          .simplif-api-key-row button { border: 0; background: transparent; color: #ff7a33; font-size: 12px; font-weight: 800; cursor: pointer; }
          .simplif-api-secret { border-color: rgba(255,90,0,.32); }
          .simplif-connect-banner-error { margin-top: 10px; color: #ffb4b4; font-size: 12px; }

          @media (max-width: 1100px) {
            .simplif-api-ready-top { align-items: flex-start; flex-direction: column; }
            .simplif-api-ready-grid { grid-template-columns: 1fr; }
          }

          /* ==========================================
             SIDEBAR STRUCTURE
          ========================================== */

          .sidebar {
            display: flex !important;
            flex-direction: column !important;
          }

          .simplif-sidebar-content {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
          }

          .sidebar-menu-area {
            flex: 1;
          }

          /* ==========================================
             LOGO
          ========================================== */

          .simplif-sidebar-brand {
            height: 72px;
            display: flex;
            align-items: center;
            overflow: hidden;
            flex-shrink: 0;
          }

          .simplif-brand-link {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            padding: 0 18px;
            overflow: hidden;
            text-decoration: none;
          }

          .simplif-brand-logo {
            display: block;
            width: 145px;
            max-width: 145px;
            height: auto;
            object-fit: contain;
            flex-shrink: 0;
          }

          .sidebar.active .simplif-brand-logo {
            display: none !important;
          }

          .sidebar.active .simplif-brand-link {
            padding: 0;
            justify-content: center;
          }

          /* ==========================================
             ACTIVE SIDEBAR
          ========================================== */

          .sidebar-menu li > a.active-page {
            background: var(--simplif-orange) !important;
            color: #ffffff !important;
          }

          .sidebar-menu li > a.active-page span,
          .sidebar-menu li > a.active-page .menu-icon,
          .sidebar-menu li > a.active-page svg {
            color: #ffffff !important;
          }

          /* ==========================================
             SIDEBAR HOVER
          ========================================== */

          .sidebar-menu li > a:not(.active-page):hover {
            background: var(--simplif-orange-soft) !important;
            color: var(--simplif-orange) !important;
          }

          .sidebar-menu li > a:not(.active-page):hover span,
          .sidebar-menu li > a:not(.active-page):hover .menu-icon,
          .sidebar-menu li > a:not(.active-page):hover svg {
            color: var(--simplif-orange) !important;
          }

          .sidebar-menu li > a.active-page:hover {
            background: var(--simplif-orange-dark) !important;
            color: #ffffff !important;
          }

          .sidebar-menu li > a.active-page:hover span,
          .sidebar-menu li > a.active-page:hover .menu-icon,
          .sidebar-menu li > a.active-page:hover svg {
            color: #ffffff !important;
          }

          /* ==========================================
             SUPPORT
          ========================================== */

          .simplif-sidebar-support {
            margin: 18px 14px 20px;
            padding: 14px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            background: #fafafa;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            flex-shrink: 0;
          }

          .simplif-support-icon {
            width: 30px;
            height: 30px;
            min-width: 30px;
            border-radius: 8px;
            background: var(--simplif-orange-soft);
            color: var(--simplif-orange);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
          }

          .simplif-support-copy {
            min-width: 0;
          }

          .simplif-support-title {
            color: #111827;
            font-size: 13px;
            font-weight: 600;
            line-height: 1.3;
            margin-bottom: 4px;
          }

          .simplif-support-email {
            color: var(--simplif-orange);
            font-size: 12px;
            font-weight: 500;
            text-decoration: none;
            word-break: break-word;
          }

          .simplif-support-email:hover {
            color: var(--simplif-orange-dark);
            text-decoration: underline;
          }

          /*
           * Hide support card when desktop sidebar is collapsed.
           */
          .sidebar.active .simplif-sidebar-support {
            display: none;
          }

          /* ==========================================
             ACCOUNT BUTTON
          ========================================== */

          .simplif-business-trigger {
            padding: 0;
            border: 0;
            outline: 0;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .simplif-business-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--simplif-orange-soft);
            color: var(--simplif-orange);
            transition: background-color 0.15s ease;
          }

          .simplif-business-trigger:hover .simplif-business-icon {
            background: #ffe1cf;
          }

          /* ==========================================
             ACCOUNT DROPDOWN
          ========================================== */

          .simplif-account-menu {
            padding-top: 12px !important;
            padding-bottom: 12px !important;
          }

          .navbar-header .dropdown-menu .dropdown-item:hover {
            color: var(--simplif-orange) !important;
          }

          /* ==========================================
             MOBILE
          ========================================== */

          @media (max-width: 1199px) {
            .navbar-search {
              display: none;
            }
          }

          @media (max-width: 767px) {
            .simplif-connect-banner {
              padding: 18px;
            }

            .simplif-connect-banner-content {
              align-items: stretch;
              flex-direction: column;
            }

            .simplif-connect-banner-message {
              align-items: flex-start;
            }

            .simplif-connect-banner-title {
              font-size: 17px;
            }

            .simplif-connect-banner-button {
              width: 100%;
            }
          }
        `}
      </style>
    </section>
  );
};

export default MasterLayout;
